import { useMemo } from "react";
import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import {
  Card,
  CardContent,
  Stack,
  TooltipContent,
  TooltipCore,
  TooltipTrigger,
  Typography,
} from "@altimateai/lego";
import {
  IconArrowRight,
  IconBolt,
  IconCheck,
  IconClock,
  IconInfoCircle,
  IconMinus,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { useAppState } from "@/modules/app";
import { formatCost } from "@/helpers/utils";
import type { AutoTuneTaskNodeData, TaskNodeStatus } from "./AutoTuneJobDag.types";
import { ClusterPanel, TaskSavingIndicator } from "./AutoTuneTaskNode.parts";
import { hasAnyCurrentSpec } from "./dagUtils";

type StatusVisual = {
  card: string;
  pill: string;
  label: string;
  Icon: typeof IconCheck;
};

// Status-driven border + pill colors. Mirrors the brand palette so
// `recommended` reads as an actionable highlight, `applied` reads as a
// successful state, `pending_apply` reads as an in-flight successful
// state (still green, but with a clock glyph), and `no_change` reads
// as quiet/neutral.
const STATUS_VISUALS: Record<TaskNodeStatus, StatusVisual> = {
  applied: {
    // ``!al-bg-bg-green`` forces ``#F0FDF4`` over the lego ``Card``
    // primitive's default ``al-bg-card`` (white). Without the
    // ``!important`` override class ordering puts ``bg-card`` last in
    // the stylesheet and the card renders white. Matches the
    // in-drawer "Potential Savings for selected Tasks" tile, which
    // uses the same ``!al-bg-bg-green`` override.
    card: "al-border-action-green !al-bg-bg-green",
    pill: "al-bg-action-green/10 al-text-action-green al-border-action-green/30",
    label: "Applied",
    Icon: IconCheck,
  },
  pending_apply: {
    card: "al-border-action-green !al-bg-bg-green",
    pill: "al-bg-action-green/10 al-text-action-green al-border-action-green/30",
    label: "Apply pending",
    Icon: IconClock,
  },
  recommended: {
    card: "al-border-stroke-light al-bg-white",
    pill: "al-bg-bg-brand al-text-action-brand al-border-stroke-brand-light",
    label: "Recommended",
    Icon: IconBolt,
  },
  no_change: {
    card: "al-border-stroke-light al-bg-muted/40 al-opacity-70",
    pill: "al-bg-bg-muted al-text-muted-foreground al-border-stroke-light",
    label: "No change",
    Icon: IconMinus,
  },
};

export type RFNodeData = AutoTuneTaskNodeData & {
  selected?: boolean;
  onSelect?: (taskKey: string, next: boolean) => void;
  onToggle: (taskKey: string, next: boolean) => void;
  // Pixel width stamped by the layout (``computeNodeWidth``) so the card
  // is wide enough to show its Worker/Driver names on one line. Same
  // value dagre reserves, so cards never overlap.
  width?: number;
};

type RFNode = Node<RFNodeData, "autoTuneTask">;

const AutoTuneTaskNode = ({ data }: NodeProps<RFNode>) => {
  const { currency } = useAppState();
  const visuals = STATUS_VISUALS[data.status];
  const isNoChange = data.status === "no_change";
  const isLocked = data.locked;
  const isRecommended = data.status === "recommended";
  const isAppliedOrPending = data.status === "applied" || data.status === "pending_apply";
  // Selectable: recommended (enable on Apply), applied/pending_apply
  // (deselect → rollback/cancel on Apply). When the container
  // withholds ``onSelect`` (e.g. flag
  // ``disableTaskSelectionAutoTuneDatabricks`` is on) the card stays
  // visible but is rendered as non-interactive — no cursor-pointer,
  // no button role, no pressed state, no keyboard activation.
  const isInteractive =
    (isRecommended || isAppliedOrPending) && !isLocked && Boolean(data.onSelect);
  // ``touched`` = user has clicked the card and its selection state
  // diverges from the server-truth default (``currentlyEnabledTaskKeys``).
  // Surface a lighter brand ring on top of the status chrome ONLY when
  // the user has explicitly interacted, so the initial render is just
  // green-on-green for every pending/applied card per spec.
  const isTouched = data.touched ?? false;
  let cardChrome = visuals.card;
  if (isTouched) {
    // Slightly darker + thicker than the original ``/40`` + ``ring-2``
    // - the user's eye was missing the ring at that subtlety.
    // ``/70`` keeps it clearly brand-blue without going full saturated,
    // and the 3 px width adds enough heft to read as "marked" without
    // crowding the card chrome.
    cardChrome = cn(visuals.card, "al-ring-2 al-ring-action-brand al-ring-inset");
  }

  // Range: high = max-savings estimate; low = high * 0.8 (20% less)
  // so the headline reads as a "we'll save at least…" promise rather
  // than a single estimate. ``no_change`` cards never carry a savings
  // or cost line: there's no recommendation to act on, so projecting a
  // monthly cost is misleading.
  //
  // We keep cent precision because sub-$1/mo savings are common on
  // short, high-frequency tasks (e.g. a stream-processing task that
  // runs in 40 s but only saves $0.06/hr). Rounding to whole dollars
  // here would hide the strip entirely for those — the strip then
  // reads as "no opportunity" instead of "tiny opportunity," and the
  // banner total drifts away from the per-card sum.
  const costLine = useMemo<{ low: number; high: number } | null>(() => {
    if (isNoChange) return null;
    const high = Math.max(data.monthlySavingsUsd ?? 0, data.monthlySavingsMaxUsd ?? 0);
    if (high <= 0) return null;
    return { low: high * 0.8, high };
  }, [data, isNoChange]);

  const isInApplySet = data.selected ?? false;
  const toggle = () => isInteractive && data.onSelect?.(data.taskKey, !isInApplySet);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <Card
      data-status={data.status}
      data-toggling={data.toggling ? "true" : undefined}
      aria-label={`Auto Tune task ${data.id}`}
      aria-busy={data.toggling ? "true" : undefined}
      aria-pressed={isInteractive ? isInApplySet : undefined}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onClick={toggle}
      onKeyDown={handleKeyDown}
      // Width is computed per node (``computeNodeWidth``) to fit the
      // Worker/Driver names on one line; dagre reserves the same value.
      style={{ width: data.width }}
      className={cn(
        // ``nodrag nopan`` keeps clicks inside the card from being
        // captured by ReactFlow's drag/pan handlers — without it, a
        // click is treated as a drag-start and ``onClick`` never fires.
        "nodrag nopan al-shadow-sm al-transition-opacity",
        cardChrome,
        data.toggling && "al-opacity-70",
        isInteractive && "al-cursor-pointer"
      )}
    >
      <Handle type="target" position={Position.Left} className="!al-bg-muted-foreground/40" />
      <CardContent className="!al-p-3">
        <Stack direction="column" gap="sm">
          {/* Top row: task name + status pill */}
          <Stack direction="row" align="center" gap="sm">
            <Typography
              variant="h4"
              weight="medium"
              className="al-truncate al-flex-1 al-min-w-0 al-text-dark"
            >
              {data.id}
            </Typography>
            <span
              className={cn(
                "al-inline-flex al-items-center al-gap-1 al-rounded-full al-border al-px-2 al-py-0.5 al-text-xs",
                visuals.pill
              )}
              aria-label={`Status: ${visuals.label}`}
            >
              <visuals.Icon size={12} />
              {visuals.label}
            </span>
          </Stack>

          {/* Middle row: Current → Proposed cluster panels.
              ``alwaysSplit`` surfaces both Worker + Driver labels on
              actionable cards (recommended / pending / applied) so the
              user sees both recs explicitly even when they happen to
              be the same instance type. ``no_change`` cards stay
              compact; tasks with no recommendation at all collapse to
              just the task name + status pill (no cluster panel) so
              the DAG can show every task in the job without
              fabricating cluster metadata we don't have. */}
          {!isNoChange ? (
            <Stack direction="row" align="center" gap="xs">
              <ClusterPanel label="Current:" spec={data.current} alwaysSplit />
              <IconArrowRight size={16} className="al-text-muted-foreground al-shrink-0" />
              <ClusterPanel label="Proposed:" spec={data.proposed} emphasis alwaysSplit />
            </Stack>
          ) : hasAnyCurrentSpec(data.current) ? (
            // Render the Current panel if the inventory JOIN gave us
            // ANY field — partial cluster info (e.g. min/max workers
            // without instance) is still informative; previously this
            // branch required a non-placeholder instance and dropped
            // the rest.
            <ClusterPanel label="Current:" spec={data.current} />
          ) : (
            <Typography size="xs" className="al-text-muted-foreground">
              No Auto Tune recommendation for this task.
            </Typography>
          )}

          {/* Bottom strip: savings range + info tooltip. ``no_change``
              cards never reach this branch — costLine is null for them
              so the slot collapses (no cost / no savings line). */}
          <Stack direction="row" align="center" justify="between" gap="sm">
            {costLine ? (
              <Stack
                direction="row"
                gap="xs"
                align="center"
                className="al-rounded-md al-bg-bg-green al-px-2 al-py-1 al-flex-1"
              >
                <Typography size="xs" className="al-text-muted-foreground">
                  Potential Savings:
                </Typography>
                <Typography size="xs" weight="medium" className="al-text-action-green">
                  {formatCost(costLine.low, currency)}–{formatCost(costLine.high, currency)}/mo
                </Typography>
              </Stack>
            ) : (
              <span />
            )}
            {/* `nodrag` / `nopan` keep these controls clickable when
                ReactFlow's interaction layer would otherwise capture
                pointer events. */}
            <Stack direction="row" align="center" gap="xs" className="nodrag nopan al-self-center">
              {data.reasoning ? (
                <TooltipCore>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Why? ${data.id}`}
                      className="al-inline-flex al-items-center al-justify-center al-h-6 al-w-6 al-text-muted-foreground hover:al-text-foreground"
                    >
                      <IconInfoCircle size={16} />
                    </button>
                  </TooltipTrigger>
                  {/* Constrain to a reasonable column so a long
                      ``reasoning`` paragraph wraps inside the popover
                      instead of stretching across the page. */}
                  <TooltipContent
                    side="top"
                    align="end"
                    // Tooltip portals to body but inherits stacking context;
                    // Sheet uses z-100, so bump above that.
                    className="al-max-w-xs al-whitespace-pre-line al-leading-snug al-z-[300]"
                  >
                    {data.reasoning}
                  </TooltipContent>
                </TooltipCore>
              ) : null}
              {data.toggling ? <TaskSavingIndicator taskId={data.id} /> : null}
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
      <Handle type="source" position={Position.Right} className="!al-bg-muted-foreground/40" />
    </Card>
  );
};

export default AutoTuneTaskNode;
